import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import visionLiveLogo from '../assets/images/visionlive.png';

const Header = ({ onBack }) => (
  <motion.nav
    initial={{ y: -20, opacity: 0 }}
    animate={{ y: 0, opacity: 1 }}
    transition={{ duration: 0.3 }}
    className="sticky top-0 z-50 bg-[#0d0d0d]/90 backdrop-blur-md border-b border-white/5"
  >
    <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
      <div className="flex items-center gap-4">
        {onBack && (
          <button
            onClick={onBack}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5 text-white/60" />
          </button>
        )}
        <a href="/" className="flex items-center gap-2">
          <img
            src={visionLiveLogo}
            alt="VisionLive"
            className="h-9 w-auto max-w-[220px] object-contain"
          />
        </a>
      </div>
    </div>
  </motion.nav>
);

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-lg font-semibold text-white mb-4">{title}</h2>
    <div className="text-white/70 text-sm leading-relaxed space-y-3">{children}</div>
  </section>
);

const TermsOfUsePage = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-[#0d0d0d] text-white font-sans antialiased">
      <Header onBack={onBack} />
      <motion.main
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="max-w-3xl mx-auto px-6 py-12"
      >
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Terms of Use</h1>
        <p className="text-white/50 text-sm mb-10">
          Last updated: January 2026 · VisionLive is operated by Ferid Brković and VTR Group d.o.o.
        </p>

        <Section title="1. Agreement">
          <p>
            These Terms of Use (“Terms”) govern your access to and use of the VisionLive website,
            virtual try-on software, API, widget, dashboard, and related services (collectively, the
            “Service”). The Service is operated by <strong>Ferid Brković and VTR Group d.o.o.</strong>.
            By using the Service, you agree to these Terms. If you do not agree, do not use the
            Service.
          </p>
        </Section>

        <Section title="2. Ownership of Software">
          <p>
            All software, code, algorithms, models, interfaces, designs, documentation, and other
            materials comprising or underlying the VisionLive Service (the “Software”) are owned
            exclusively by <strong>Ferid Brković and VTR Group d.o.o.</strong>. All rights, title, and
            interest in and to the Software, including all intellectual property rights, are and
            shall remain vested in the owner. No ownership rights are transferred to you by your
            use of the Service or by these Terms.
          </p>
        </Section>

        <Section title="3. License and Restrictions">
          <p>
            Subject to your compliance with these Terms and any applicable subscription plan, you
            are granted a limited, non-exclusive, non-transferable, revocable license to access
            and use the Service solely for your own internal business or personal use, in the
            manner permitted by the Service and your plan.
          </p>
          <p>You agree <strong>not</strong> to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Redistribute, sublicense, resell, lease, or otherwise distribute the Software or any part of the Service to any third party;</li>
            <li>Copy, modify, adapt, reverse engineer, decompile, disassemble, or create derivative works of the Software;</li>
            <li>Remove or alter any proprietary notices, labels, or marks on the Service;</li>
            <li>Use the Service for any unlawful purpose or in violation of any applicable law;</li>
            <li>Circumvent or attempt to circumvent any access controls, usage limits, or security measures;</li>
            <li>Use the Service to build a competing product or service.</li>
          </ul>
        </Section>

        <Section title="4. Subscriptions and Payment">
          <p>
            Paid plans and subscriptions are offered through Whop.com or other designated
            platforms. Subscription terms, billing, refunds, and cancellations are governed by
            the applicable platform's terms. You are responsible for maintaining accurate account and payment
            information.
          </p>
        </Section>

        <Section title="5. Acceptable Use">
          <p>
            You must use the Service in accordance with these Terms and any applicable
            acceptable-use or content policies. You are responsible for all activity under your
            account and for ensuring that your use (including any integration, embed, or
            end-user interaction) complies with applicable laws, including those related to
            privacy, data protection, and intellectual property.
          </p>
        </Section>

        <Section title="6. Privacy and Data">
          <p>
            Our collection and use of personal and other data in connection with the Service are
            described in our <a href="/privacy" className="text-[#3B82F6] hover:underline">Privacy Policy</a>.
            By using the Service, you also agree to the Privacy Policy.
          </p>
        </Section>

        <Section title="7. Disclaimer of Warranties">
          <p>
            The Service is provided “as is” and “as available.” To the fullest extent permitted
            by law, <strong>Ferid Brković and VTR Group d.o.o.</strong> disclaim all warranties,
            express or implied, including merchantability, fitness for a particular purpose, and
            non-infringement. We do not warrant that the Service will be uninterrupted, error-free,
            or secure.
          </p>
        </Section>

        <Section title="8. Limitation of Liability">
          <p>
            To the maximum extent permitted by law, <strong>Ferid Brković and VTR Group d.o.o.</strong>{' '}
            shall not be liable for any indirect, incidental, special, consequential, or punitive
            damages, or for any loss of profits, data, goodwill, or business opportunity, arising
            from or related to your use of the Service. In no event shall our aggregate liability
            exceed the amounts actually paid by you for the Service in the twelve (12) months
            preceding the claim, or one hundred euros (€100), whichever is greater.
          </p>
        </Section>

        <Section title="9. Termination">
          <p>
            We may suspend or terminate your access to the Service at any time, with or without
            cause or notice, including for breach of these Terms. Upon termination, your license
            to use the Service ends immediately. Provisions that by their nature should survive
            (including ownership, restrictions, disclaimers, and limitation of liability) will
            survive termination.
          </p>
        </Section>

        <Section title="10. Changes">
          <p>
            We may update these Terms from time to time. We will post the updated Terms on this
            page and update the “Last updated” date. Continued use of the Service after changes
            constitutes acceptance of the revised Terms. If you do not agree, you must stop using
            the Service.
          </p>
        </Section>

        <Section title="11. General">
          <p>
            These Terms constitute the entire agreement between you and Ferid Brković and VTR
            Group d.o.o., regarding the Service. They are governed by the
            laws of the jurisdiction in which the operator is established, without regard to
            conflict-of-law principles.
            Any dispute shall be resolved in the courts of that jurisdiction. If any provision
            is held invalid, the remaining provisions remain in effect.
          </p>
        </Section>

        <Section title="12. Contact">
          <p>
            For questions about these Terms, contact us through the channels provided on the
            VisionLive website or via the Whop.com platform through which you subscribed.
          </p>
        </Section>

        <div className="mt-14 pt-8 border-t border-white/10">
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} VisionLive. All rights reserved. Ferid Brković and VTR Group d.o.o.
          </p>
        </div>
      </motion.main>
    </div>
  );
};

export default TermsOfUsePage;
